import { useEffect, useRef } from 'react';
import i18n from './index';

const POLL_INTERVAL_MS = 60_000;

/**
 * Polls /api/i18n/version every 60s. Whenever the latest updatedAt changes,
 * triggers `i18n.reloadResources()` so all loaded namespaces are refetched
 * and a re-render is emitted via the `languageChanged` event.
 *
 * Mount this once at the app shell level. It pauses while the tab is hidden
 * and ticks immediately when the tab becomes visible again.
 */
export function useTranslationVersion(): void {
  const lastVersionRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await fetch('/api/i18n/version', { credentials: 'omit' });
        if (!res.ok) return;
        const body = (await res.json()) as { updatedAt: string | null };
        const version = body.updatedAt;
        if (lastVersionRef.current !== null && version && lastVersionRef.current !== version) {
          await i18n.reloadResources();
          // Force consumers using `useTranslation()` to re-render.
          i18n.emit('languageChanged', i18n.language);
        }
        if (version) lastVersionRef.current = version;
      } catch {
        // Silent — version polling failures must never disrupt the UI.
      }
    };

    void tick();
    const id = window.setInterval(tick, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
